import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShare } from '@fortawesome/pro-light-svg-icons'
import { faToggleOff, faToggleOn } from '@fortawesome/pro-solid-svg-icons'

import {
  Button,
  Columns,
  LoadingModule,
  SaveFormModule,
  Tools,
} from '@domain-group/suit-theme'

import { generateRedirectUrl } from '@domain-group/javascript-utils'

import { generateUrls } from '@hooks/useAgency'
import useCampaign, { useCampaignForm } from '@hooks/useCampaign'
import useCurrentUser from '@hooks/useCurrentUser'

import CampaignTeam from '@components/CampaignTeam'
import EntityActivity from '@components/EntityActivity'
import LinkToEntityModule from '@components/LinkToEntityModule'
import MessagePanel from '@components/MessagePanel'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import PageContext from '@contexts/pageContext'

import { providerRoles } from '@functions/role'
import PageLayout from '../../../../components/PageLayout'

import CampaignAddress from '../CampaignAddress'
import CampaignPortalSettings from '../CampaignPortalSettings'
import CampaignPortalSyncs from '../CampaignPortalSyncs'
import OrderItems from '../OrderItems'

const PageContent = (props: { campaignId: number}) => {
  const { campaignId } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      updateCampaign,
    },
  } = pageContext

  const campaignPayload = useCampaign({ id: campaignId })

  const {
    agency,
    callbacks: {
      manageCampaignTestStatusModal,
      updateCampaign: updateFn,
      shareProjectModal,
    },
    campaign,
    loading,
    updating,
  } = campaignPayload

  const campaignFormPayload = useCampaignForm(campaign, { validateOn: 'change' })

  const { entityState, saveEnabled } = campaignFormPayload

  const {
    title: agencyTitle,
    business_name: agencyBusinessName,
    short_name: agencyShortName,
  } = agency || {}

  const { editAgencyUrl } = generateUrls(agency)

  const updateCampaignPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'Campaign Updated',
  }

  const { isAdmin, provider } = useCurrentUser()

  return (
    <PageLayout entityPayload={campaignPayload} activeTabBarItemKey="details">
      <Columns.Main>
        <LoadingModule loading={loading} times={3} />

        {!loading && (
          <>
            <MessagePanel
              entityProperty={campaign.sandbox}
              title="Test Campaign"
              description={isAdmin
                ? `This campaign has been set as a Test Campaign.
                Please refer to campaign conversation prior to reactivating.`
                : undefined}
              marginBottom="large"
            />

            {/*
             // @ts-expect-error TS2740 */}
            <CampaignAddress agency={agency} campaign={campaign} campaignFormPayload={campaignFormPayload} />
          </>
        )}

        <ScopedConditionalContent permittedRoleTitles={['Provider Director', 'Provider Employee']}>
          <OrderItems campaignId={campaignId} />
        </ScopedConditionalContent>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          {!loading && (
            <CampaignPortalSettings campaign={campaign} campaignFormPayload={campaignFormPayload} />
          )}

          <CampaignPortalSyncs campaignId={campaignId} />

          <EntityActivity subjectId={campaignId} subjectType="Campaign" />

          {!loading && (
            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                //@ts-expect-error
                marginLeft="auto"
                onClick={() => updateCampaign(updateCampaignPayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>
          )}
        </ScopedConditionalContent>
      </Columns.Main>

      <Columns.Sidebar>
        <Tools>
          <ScopedConditionalContent
            permittedRoleTitles={providerRoles}
          >
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faShare} />}
              // @ts-expect-error TS2345
              onClick={() => shareProjectModal(provider)}
              size="medium"
            >
              Share Project
            </Button>
          </ScopedConditionalContent>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={campaign.sandbox ? faToggleOff : faToggleOn} />}
              onClick={() => manageCampaignTestStatusModal()}
              size="medium"
            >
              {campaign.sandbox ? 'Reactivate Campaign' : 'Set as Test Campaign' }
            </Button>
          </ScopedConditionalContent>
        </Tools>

        <LinkToEntityModule
          entityTitle={agencyTitle || ''}
          goToHref={isAdmin ? `${editAgencyUrl}?redirect=${generateRedirectUrl()}` : ''}
          loading={loading}
          moduleTitle="Agency"
          secondaryText={agencyShortName || agencyBusinessName}
        />

        {!loading && (<CampaignTeam campaign={campaign} />)}
      </Columns.Sidebar>
    </PageLayout>
  )
}

export default PageContent
